/*
 * <recently-viewed-products> — populates the Recently Viewed section
 * (sections/recently-viewed-products.liquid) from the rv:handles localStorage
 * list that recently-viewed.js maintains on every product page.
 *
 * Cards are fetched per handle via the Section Rendering API
 * (?section_id=recently-viewed-product-card) so they are the exact same
 * card-product markup as the featured-collection "RECOMMENDED FOR YOU" grid.
 * Fetching starts when the section nears the viewport; the section stays
 * hidden when the shopper has no history.
 */
(() => {
  if (customElements.get('recently-viewed-products')) return;

  const STORAGE_KEY = 'rv:handles';
  const CARD_SECTION_ID = 'recently-viewed-product-card';

  const normalizeHandle = (handle) => (handle || '').toString().trim().toLowerCase();

  const readHandles = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const seen = new Set();
      const cleaned = [];
      parsed.forEach((entry) => {
        const handle = normalizeHandle(entry);
        if (!handle || seen.has(handle)) return;
        seen.add(handle);
        cleaned.push(handle);
      });
      return cleaned;
    } catch (_) {
      return [];
    }
  };

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.grid = this.querySelector('[id^="Slider-"]');
      this.sliderComponent = this.querySelector('slider-component');
      this.sliderButtons = this.querySelector('.slider-buttons');
      this.titleWrapper = this.querySelector('.collection__title');

      if (!this.grid) return;

      // In the editor, load immediately so the merchant sees the section while
      // configuring it. On the storefront, wait until the section nears the
      // viewport to avoid fetches the shopper never scrolls to.
      if (this.hasAttribute('data-design-mode') || !('IntersectionObserver' in window)) {
        this.loadProducts();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.loadProducts();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      // This element is [hidden] (display: none) until products load, so it has
      // no box and can NEVER intersect — observing it deadlocks the section
      // (hidden-until-loaded vs. load-when-visible). Observe the section
      // wrapper instead: it stays in layout as a zero-height sentinel at the
      // same page position, which IntersectionObserver handles fine.
      observer.observe(this.closest('.shopify-section') || this.parentElement || this);
    }

    async loadProducts() {
      const currentHandle = normalizeHandle(this.dataset.currentProductHandle);
      const maxProducts = parseInt(this.dataset.productsToShow, 10) || 6;
      const handles = readHandles()
        .filter((handle) => handle !== currentHandle)
        .slice(0, maxProducts);

      // No history: stay hidden on the storefront (editor keeps placeholders).
      if (!handles.length) return;

      const results = await Promise.allSettled(handles.map((handle) => this.fetchCard(handle)));
      const cards = results
        .filter((result) => result.status === 'fulfilled' && result.value)
        .map((result) => result.value);

      if (!cards.length) return;

      this.renderCards(cards);
    }

    async fetchCard(handle) {
      const response = await fetch(
        `/products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION_ID}`,
        { credentials: 'same-origin' }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = text;
      return wrapper.querySelector('.product-card-wrapper');
    }

    renderCards(cards) {
      const sectionId = this.dataset.sectionId;
      const columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 6;
      const columnsMobile = parseInt(this.dataset.columnsMobile, 10) || 2;
      const showDesktopSlider =
        this.hasAttribute('data-enable-desktop-slider') && cards.length > columnsDesktop;
      const showMobileSlider =
        this.hasAttribute('data-swipe-on-mobile') && cards.length > columnsMobile;
      const isSlider = showDesktopSlider || showMobileSlider;

      const fragment = document.createDocumentFragment();
      cards.forEach((card, index) => {
        const item = document.createElement('li');
        item.id = `Slide-${sectionId}-${index + 1}`;
        item.className = `grid__item grid__item-${index + 1}${isSlider ? ' slider__slide' : ''}`;
        item.appendChild(card);
        fragment.appendChild(item);
      });

      this.grid.innerHTML = '';
      this.grid.appendChild(fragment);

      // Mirror featured-collection's conditional slider classes.
      if (isSlider) this.grid.classList.add('slider');
      if (showDesktopSlider) {
        this.grid.classList.add('slider--desktop');
        if (this.sliderComponent) {
          this.sliderComponent.classList.add('slider-component-desktop');
          this.sliderComponent.classList.remove('page-width-desktop');
        }
        if (this.titleWrapper) this.titleWrapper.classList.add('collection__title--desktop-slider');
      }
      if (showMobileSlider) {
        this.grid.classList.add('slider--tablet', 'grid--peek');
        if (this.sliderComponent) this.sliderComponent.classList.remove('page-width');
        if (this.titleWrapper) this.titleWrapper.classList.add('title-wrapper--self-padded-tablet-down');
      }
      if (isSlider && this.sliderButtons) {
        this.sliderButtons.hidden = false;
        // Parity with featured-collection ("Recommended For You"): when only
        // the mobile slider is active, the desktop grid doesn't slide, so the
        // strip has no job at >=990px — and overlaying it across the cards is
        // what hid the per-card image-slider arrows under the cursor.
        this.sliderButtons.classList.toggle('large-up-hide', !showDesktopSlider);
      }

      this.removeAttribute('hidden');

      if (this.sliderComponent && typeof this.sliderComponent.resetPages === 'function') {
        this.sliderComponent.resetPages();
      }
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();
